import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Clock,
  Link as LinkIcon,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  Search,
  ChevronDown,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * /settings/worksection/users — map Worksection users to portal staff.
 *
 * Each WS user shows their derived hourly rate (portal employee salary in USD / 160) once
 * linked. Salary is read from db.employees (mix of UAH/USD currency, converted via
 * PrivatBank rates). Optional `hourly_rate_override` (in USD) lets admin set an
 * explicit rate for people not in portal, freelancers, or custom arrangements.
 *
 * Phase 1 surface — no cost is written to portal tasks yet. This is just the
 * mapping table that Phase 3 cost recompute will read from.
 */
const WorksectionUsersPage = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]); // WS users with link + portal_staff + derived rate
  const [staffCandidates, setStaffCandidates] = useState([]); // portal employees with salary>0 (any currency)
  const [search, setSearch] = useState("");
  const [filterUnmapped, setFilterUnmapped] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([
        api.get("/worksection/users"),
        api.get("/worksection/staff-candidates"),
      ]);
      setUsers(u.data || []);
      setStaffCandidates(s.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Worksection users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || []).filter((u) => {
      if (filterUnmapped && u.link) return false;
      if (!q) return true;
      return (
        (u.ws_name || "").toLowerCase().includes(q) ||
        (u.ws_email || "").toLowerCase().includes(q) ||
        (u.portal_staff?.name || "").toLowerCase().includes(q)
      );
    });
  }, [users, search, filterUnmapped]);

  const stats = useMemo(() => {
    const total = users.length;
    const linked = users.filter((u) => !!u.link).length;
    const withRate = users.filter((u) => u.effective_hourly_rate > 0).length;
    return { total, linked, unmapped: total - linked, withRate };
  }, [users]);

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-50 flex items-center gap-2">
            <Clock className="w-6 h-6 text-emerald-400" />
            Worksection users
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Link each Worksection user to a portal staff record so their logged hours can be converted to cost.
            Rate defaults to <code className="bg-zinc-800 px-1 rounded text-zinc-300">salary_usd / 160</code> from
            <code className="bg-zinc-800 px-1 rounded text-zinc-300">db.employees</code> (UAH/EUR salaries auto-converted to USD).
            Override only for special cases.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="border-zinc-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Reload
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KpiCard label="Total WS users" value={stats.total} color="zinc" />
        <KpiCard label="Linked to staff" value={stats.linked} color="emerald" />
        <KpiCard label="Unmapped" value={stats.unmapped} color={stats.unmapped > 0 ? "amber" : "zinc"} />
        <KpiCard label="With effective rate" value={stats.withRate} color="cyan" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            placeholder="Search by WS name, email, or portal staff name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-zinc-900 border-zinc-700 pl-10"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={filterUnmapped}
            onChange={(e) => setFilterUnmapped(e.target.checked)}
            className="accent-emerald-500"
          />
          Unmapped only
        </label>
      </div>

      {/* Users table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50 text-base">
            Worksection users ({filtered.length})
          </CardTitle>
          <CardDescription className="text-zinc-400 text-xs">
            Pick a portal staff member from the dropdown; rate auto-computes. Optionally set a custom hourly rate
            (USD/hour) to override the salary-derived rate.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-zinc-500 py-12 text-sm">
              {users.length === 0
                ? "No Worksection users yet — go to Integrations and run sync first."
                : "No users match the current filter."}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((u) => (
                <WsUserRow
                  key={u.ws_user_id}
                  user={u}
                  staffCandidates={staffCandidates}
                  onChanged={load}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ---------- subcomponents ----------

const KpiCard = ({ label, value, color }) => {
  const colorMap = {
    zinc: "border-zinc-800 text-zinc-300",
    emerald: "border-emerald-500/30 text-emerald-300",
    amber: "border-amber-500/30 text-amber-300",
    cyan: "border-cyan-500/30 text-cyan-300",
  };
  return (
    <div className={`bg-zinc-900/50 border rounded-lg p-3 ${colorMap[color] || colorMap.zinc}`}>
      <div className="text-xs text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
};

const WsUserRow = ({ user, staffCandidates, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(user.link?.portal_staff_id || "");
  const [override, setOverride] = useState(
    user.link?.hourly_rate_override != null ? String(user.link.hourly_rate_override) : ""
  );
  const [notes, setNotes] = useState(user.link?.notes || "");
  const [saving, setSaving] = useState(false);
  const [staffPickerOpen, setStaffPickerOpen] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");

  const matchedStaff = staffCandidates.find((s) => s.id === selectedStaffId);
  const filteredStaff = useMemo(() => {
    const q = staffSearch.trim().toLowerCase();
    if (!q) return staffCandidates;
    return staffCandidates.filter(
      (s) => (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q)
    );
  }, [staffCandidates, staffSearch]);

  const derivedRate = matchedStaff?.derived_hourly_rate || 0;
  const overrideNum = override === "" ? null : Number(override);
  const effectiveRate =
    overrideNum != null && !Number.isNaN(overrideNum) ? overrideNum : derivedRate;

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/worksection/user-links", {
        ws_user_id: user.ws_user_id,
        portal_staff_id: selectedStaffId || null,
        hourly_rate_override:
          override === "" ? null : Number.isNaN(Number(override)) ? null : Number(override),
        notes: notes || null,
      });
      toast.success(`Linked ${user.ws_name}`);
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Unlink ${user.ws_name}? Their hours will stop being attributed to portal cost.`))
      return;
    setSaving(true);
    try {
      await api.delete(`/worksection/user-links/${user.ws_user_id}`);
      toast.success("Unlinked");
      setSelectedStaffId("");
      setOverride("");
      setNotes("");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const sourceLabel = user.rate_source === "override"
    ? "override"
    : user.rate_source === "staff"
      ? "salary/160"
      : "—";
  const sourceColor = user.rate_source === "override"
    ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
    : user.rate_source === "staff"
      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
      : "bg-zinc-800 text-zinc-500 border-zinc-700";

  return (
    <div className="border border-zinc-800 rounded-lg bg-zinc-950/40">
      {/* Collapsed summary */}
      <div className="flex items-center gap-4 p-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-zinc-200 font-medium truncate">{user.ws_name || "—"}</span>
            {user.link ? (
              <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] uppercase tracking-wide">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Linked
              </Badge>
            ) : (
              <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] uppercase tracking-wide">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Unmapped
              </Badge>
            )}
          </div>
          <div className="text-xs text-zinc-500 truncate">
            {user.ws_email || "(no email)"} · WS id {user.ws_user_id}
            {user.portal_staff ? (
              <>
                {" · → "}
                <span className="text-zinc-300">{user.portal_staff.name}</span>
                <span className="text-zinc-500">
                  {" "}
                  ({user.portal_staff.position || "?"})
                </span>
              </>
            ) : null}
          </div>
        </div>

        <div className="text-right">
          <div className="text-xs text-zinc-500">Effective rate</div>
          <div className="text-zinc-200 font-mono">
            ${(user.effective_hourly_rate || 0).toFixed(2)}/h
          </div>
          <Badge className={`text-[10px] mt-1 ${sourceColor}`}>{sourceLabel}</Badge>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditing((v) => !v)}
          className="border-zinc-700"
        >
          <LinkIcon className="w-3.5 h-3.5 mr-1.5" />
          {editing ? "Close" : "Edit link"}
        </Button>
      </div>

      {/* Expanded editor */}
      {editing && (
        <div className="border-t border-zinc-800 p-4 space-y-4 bg-zinc-950/60">
          {/* Portal staff picker */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs uppercase tracking-wide">Portal staff</Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setStaffPickerOpen((v) => !v)}
                className="w-full flex items-center justify-between bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-left hover:border-zinc-600"
              >
                {selectedStaffId && matchedStaff ? (
                  <span className="text-zinc-200">
                    {matchedStaff.name}{" "}
                    <span className="text-zinc-500 text-xs">
                      · {matchedStaff.position || "?"} · ${matchedStaff.derived_hourly_rate.toFixed(2)}/h
                    </span>
                  </span>
                ) : (
                  <span className="text-zinc-500 italic">— No portal staff linked —</span>
                )}
                <ChevronDown className="w-4 h-4 text-zinc-500" />
              </button>
              {staffPickerOpen && (
                <div className="absolute z-10 mt-1 w-full bg-zinc-900 border border-zinc-700 rounded shadow-xl">
                  <div className="p-2 border-b border-zinc-800">
                    <Input
                      autoFocus
                      placeholder="Search portal staff…"
                      value={staffSearch}
                      onChange={(e) => setStaffSearch(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-sm"
                    />
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedStaffId("");
                        setStaffPickerOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs italic text-zinc-500 hover:bg-zinc-800"
                    >
                      — Clear (no portal staff link) —
                    </button>
                    {filteredStaff.length === 0 ? (
                      <div className="px-3 py-3 text-xs text-zinc-500 italic">No matches</div>
                    ) : (
                      filteredStaff.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            setSelectedStaffId(s.id);
                            setStaffPickerOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 hover:bg-zinc-800 ${selectedStaffId === s.id ? "bg-zinc-800" : ""}`}
                        >
                          <div className="text-sm text-zinc-200">{s.name}</div>
                          <div className="text-xs text-zinc-500">
                            {s.email} · {s.position || "?"} · ${s.derived_hourly_rate.toFixed(2)}/h ({s.salary_native.toFixed(0)} {s.salary_currency}/mo{s.salary_currency !== "USD" ? ` ≈ $${s.salary_usd.toFixed(0)}` : ""})
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <p className="text-[11px] text-zinc-500">
              Only employees with <code className="bg-zinc-800 px-1 rounded">salary &gt; 0</code> are shown. Piece-rate
              artists are excluded by design — their cost comes from <code className="bg-zinc-800 px-1 rounded">agreed_price</code>.
              Salaries in UAH/EUR are auto-converted to USD via PrivatBank rates.
            </p>
          </div>

          {/* Override rate */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-wide">
                Hourly rate override (USD/hour)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder={derivedRate > 0 ? `Auto: $${derivedRate.toFixed(2)}/h (from salary)` : "—"}
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                className="bg-zinc-900 border-zinc-700 text-sm font-mono"
              />
              <p className="text-[11px] text-zinc-500">
                Leave empty to use salary/160. Set explicit value for non-portal users or special arrangements.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-wide">Effective rate (preview)</Label>
              <div className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded text-zinc-200 font-mono">
                ${effectiveRate.toFixed(2)}/h
              </div>
              <p className="text-[11px] text-zinc-500">
                Source: {overrideNum != null && !Number.isNaN(overrideNum) ? "override (USD)" : matchedStaff ? "salary/160 (USD)" : "—"}
                {matchedStaff && (
                  <>
                    {" · "}
                    portal salary <span className="font-mono">
                      {matchedStaff.salary_native.toFixed(0)} {matchedStaff.salary_currency}/mo
                      {matchedStaff.salary_currency !== "USD" && ` ≈ $${matchedStaff.salary_usd.toFixed(0)}`}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs uppercase tracking-wide">Notes (optional)</Label>
            <Input
              placeholder="e.g. 'Part-time, billed at $25/h per agreement Feb 2026'"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-zinc-900 border-zinc-700 text-sm"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
            {user.link && (
              <Button variant="outline" onClick={remove} disabled={saving} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Unlink
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditing(false)} disabled={saving} className="border-zinc-700">
              <X className="w-3.5 h-3.5 mr-1.5" />
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500">
              {saving ? "Saving…" : "Save link"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorksectionUsersPage;
